import { useEffect, useState } from 'react'
import { Save, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import { useGetShippingFeeSettings, useUpdateShippingFeeSettings } from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'

interface FormFields {
  shipping_actual_cost_max: string
  shipping_fixed_rate_max: string
  shipping_fixed_rate: string
  delivery_free_threshold: string
  delivery_fixed_rate: string
}

const EMPTY_FORM: FormFields = {
  shipping_actual_cost_max: '',
  shipping_fixed_rate_max: '',
  shipping_fixed_rate: '',
  delivery_free_threshold: '',
  delivery_fixed_rate: '',
}

export default function ShippingFeeSettings() {
  const getShippingFeeSettings = useGetShippingFeeSettings()
  const updateShippingFeeSettings = useUpdateShippingFeeSettings()
  const [form, setForm] = useState<FormFields>(EMPTY_FORM)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getShippingFeeSettings
      .trigger()
      .then((data: unknown) => {
        const row = (data ?? {}) as Partial<Record<keyof FormFields, number>>
        setForm({
          shipping_actual_cost_max: String(row.shipping_actual_cost_max ?? 250),
          shipping_fixed_rate_max: String(row.shipping_fixed_rate_max ?? 500),
          shipping_fixed_rate: String(row.shipping_fixed_rate ?? 30),
          delivery_free_threshold: String(row.delivery_free_threshold ?? 500),
          delivery_fixed_rate: String(row.delivery_fixed_rate ?? 30),
        })
      })
      .catch(() => toast.error('Failed to load shipping fee settings'))
      .finally(() => setLoaded(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const field = (key: keyof FormFields, label: string, prefix?: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`fee-${key}`}>{label}</Label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{prefix}</span>
        )}
        <Input
          id={`fee-${key}`}
          type="number"
          min={0}
          step="0.01"
          className={prefix ? 'pl-6' : ''}
          value={form[key]}
          disabled={!loaded}
          onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        />
      </div>
    </div>
  )

  const handleSave = async () => {
    const parsed = {
      shipping_actual_cost_max: Number(form.shipping_actual_cost_max),
      shipping_fixed_rate_max: Number(form.shipping_fixed_rate_max),
      shipping_fixed_rate: Number(form.shipping_fixed_rate),
      delivery_free_threshold: Number(form.delivery_free_threshold),
      delivery_fixed_rate: Number(form.delivery_fixed_rate),
    }
    if (Object.values(parsed).some(v => !Number.isFinite(v) || v < 0)) {
      toast.error('All values must be non-negative numbers')
      return
    }
    if (parsed.shipping_actual_cost_max > parsed.shipping_fixed_rate_max) {
      toast.error('Shipping actual-cost threshold cannot exceed the shipping fixed-rate threshold')
      return
    }
    setSaving(true)
    try {
      await updateShippingFeeSettings.trigger(parsed)
      toast.success('Shipping fee settings saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save shipping fee settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shipping Fee Rules</CardTitle>
        <CardDescription>
          Controls the shipping fee reported for each completed order, based on the customer&apos;s delivery
          method and the order total.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">Shipping customers</h3>
          <p className="text-xs text-muted-foreground"><HelpText>
            Below the first threshold, the actual label cost is charged. Between the two thresholds, the fixed
            rate applies. Above the second threshold, shipping is free.
          </HelpText></p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field('shipping_actual_cost_max', 'Actual-cost threshold', '$')}
            {field('shipping_fixed_rate_max', 'Free-shipping threshold', '$')}
            {field('shipping_fixed_rate', 'Fixed rate', '$')}
          </div>
        </div>

        <div className="border-t pt-4 space-y-3">
          <h3 className="text-sm font-medium text-foreground">Delivery customers</h3>
          <p className="text-xs text-muted-foreground">
            Below the threshold, the fixed rate applies. At or above it, delivery is free.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field('delivery_free_threshold', 'Free-delivery threshold', '$')}
            {field('delivery_fixed_rate', 'Fixed rate', '$')}
          </div>
        </div>

        <p className="text-xs text-muted-foreground border-t pt-4">
          Free delivery customers are never charged a shipping fee.
        </p>

        <div className="flex justify-end">
          <Button onClick={() => void handleSave()} disabled={saving || !loaded} className="gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Shipping Fee Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
